import { useState, useEffect } from 'react';
import { useParams, generatePath } from 'react-router-dom';
import { Button, Box, Typography, Grid, Stack, Container } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useStoreUnitActions } from 'src/redux/helpers';
import solutionsStore from 'src/pages/Solutions/redux';
import { pepApiRequest, RequestMethod, useStatefulApi } from 'src/services';
import {
  API_DASHBOARD_WIDGET_DELETE,
  API_DASHBOARD_WIDGET_UPDATE,
} from 'src/pages/Solutions/constants';
import { PageCardLoader } from 'src/components/PageCardLayout';
import { AlertType, useAlert } from '../../../../../components/AlertsProvider';
import ModalDialog, {
  ModalCloseHandler,
  ModalCloseReason,
} from '../../../../../components/ModalDialog';
import { WidgetRenderer } from '../../../../../components/widgets/WidgetRenderer';
import { ReusableWidgetConfigModal } from './ReusableWidgetConfigModal';
import { ResizableWidget } from '../../../../../components/widgets/ResizableWidget';
import {
  DashboardState,
  WidgetConfig,
} from '../../../../../components/widgets/widget';
import { useSolutionWidgetList } from '../../../redux/selectors';
import { DashboardItem } from '../../../types/solutionAssetHierarchy';
import { prepareWidgetPayload } from '../utils';

type Props = {
  item?: any;
  onClose: ModalCloseHandler;
};

export const Dashboard = ({ item, onClose }: Props) => {
  const { showAlert } = useAlert();
  const { solutionId } = useParams<{ solutionId: string }>();
  const [widgetListData, widgetListLoading] = useSolutionWidgetList();
  const actions = useStoreUnitActions(solutionsStore);

  const [dashboardState, setDashboardState] = useState<DashboardState>({
    widgets: [],
    isEditing: false,
  });

  const [unsavedWidgets, setUnsavedWidgets] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (solutionId && item?.id && item?.twinId) {
      setDashboardState((prev) => ({
        ...prev,
        widgets: [],
      }));

      actions.WidgetList({
        solutionId,
        dashboardId: item.id,
        twinId: item.twinId,
      });
    }
  }, [solutionId, item?.id, item?.twinId]);

  useEffect(() => {
    if (widgetListData && item?.id) {
      let allWidgets: any[] = [];

      if (Array.isArray(widgetListData)) {
        allWidgets = widgetListData.flatMap((dashboard: DashboardItem) => {
          if (dashboard.id === item.id) {
            return dashboard.widgets || [];
          }
          return [];
        });
      } else if (
        widgetListData &&
        typeof widgetListData === 'object' &&
        'widgets' in widgetListData &&
        Array.isArray((widgetListData as any).widgets)
      ) {
        if ((widgetListData as any).id === item.id) {
          allWidgets = (widgetListData as any).widgets;
        } else {
          allWidgets = [];
        }
      }

      if (allWidgets.length > 0) {
        const transformedWidgets = allWidgets.map((serverWidget: any) => {
          const getChartTypeString = (chartType: any): string => {
            if (typeof chartType === 'string') {
              return chartType;
            }
            const chartTypeMap: { [key: number]: string } = {
              0: 'line',
              1: 'bar',
              2: 'area',
              3: 'pie',
              4: 'scatter',
              5: 'radar',
              6: 'table',
              7: 'metric',
            };
            return chartTypeMap[chartType] || 'line';
          };

          return {
            id: serverWidget.id || `widget-${Date.now()}`,
            title: serverWidget.title || 'Untitled Widget',
            chartType: getChartTypeString(serverWidget.chartType),
            dataSource: {
              type: serverWidget.dataSource?.type || 'apiInternal',
              method: serverWidget.dataSource?.method || 'POST',
              endpoint: serverWidget.dataSource?.endpoint || '',
              headers: serverWidget.dataSource?.headers || [],
              body: serverWidget.dataSource?.body || '',
              refreshRate: serverWidget.dataSource?.refreshRate || 30,
            },
            chartConfiguration: {
              xAxis: serverWidget.chartConfiguration?.xAxis || {
                columnName: '',
                label: '',
              },
              yAxis: serverWidget.chartConfiguration?.yAxis || {
                columnName: '',
                label: '',
              },
              dataSeries: serverWidget.chartConfiguration?.dataSeries || [],
              showLegend: serverWidget.chartConfiguration?.showLegend ?? true,
            },
            layout: {
              width: serverWidget.layout?.width || 6,
              height: serverWidget.layout?.height || 4,
              order: serverWidget.layout?.order || 1,
            },
          };
        });

        setDashboardState((prev) => ({
          ...prev,
          widgets: transformedWidgets,
        }));
      }
    }
  }, [item?.id, widgetListData]);

  useEffect(() => {
    if (item?.widgets && (!widgetListData || widgetListData.length === 0)) {
      setDashboardState((prev) => ({
        ...prev,
        widgets: item.widgets,
      }));
    }
  }, [item?.widgets, widgetListData]);

  const [configModal, setConfigModal] = useState<{
    isOpen: boolean;
    widget?: WidgetConfig;
  }>({ isOpen: false });

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    widget?: WidgetConfig;
  }>({ isOpen: false });

  const toggleEditMode = () => {
    setDashboardState((prev) => ({
      ...prev,
      isEditing: !prev.isEditing,
    }));

    if (!dashboardState.isEditing) {
      showAlert({
        type: AlertType.Info,
        title: 'Edit Mode Enabled',
        text: 'You can now configure and remove widgets. Resize from any edge or corner.',
      });
    }
  };

  const handleAddWidget = () => {
    setConfigModal({ isOpen: true });
  };

  const handleEditWidget = (widget: WidgetConfig) => {
    setConfigModal({ isOpen: true, widget });
  };

  const handleSaveWidget = (widget: WidgetConfig) => {
    setDashboardState((prev) => {
      const existingIndex = prev.widgets.findIndex((w) => w.id === widget.id);
      let newWidgets;

      if (existingIndex >= 0) {
        newWidgets = prev.widgets.map((w) => (w.id === widget.id ? widget : w));
      } else {
        newWidgets = [...prev.widgets, widget];
      }

      newWidgets.sort((a, b) => (a.layout.order || 0) - (b.layout.order || 0));

      return { ...prev, widgets: newWidgets };
    });
  };

  const handleSaveWidgetLayout = async (widget: WidgetConfig) => {
    try {
      const apiUrl = generatePath(API_DASHBOARD_WIDGET_UPDATE, {
        solutionId,
        dashboardId: item?.id || 'default',
        widgetId: widget.id,
        twinId: item?.twinId,
      });

      await pepApiRequest({
        method: RequestMethod.Put,
        url: apiUrl,
        params: prepareWidgetPayload(widget),
      });

      setUnsavedWidgets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(widget.id);
        return newSet;
      });

      showAlert({
        type: AlertType.Success,
        title: 'Widget Layout Saved',
        text: `Layout changes for "${widget.title}" have been saved successfully`,
      });
    } catch (error) {
      console.error('Failed to save widget layout:', error);
      showAlert({
        type: AlertType.Error,
        title: 'Save Failed',
        text: 'Failed to save widget layout changes',
      });
    }
  };

  const handleRemoveWidget = (widgetId: string) => {
    const widgetToDelete = dashboardState.widgets.find(
      (w) => w.id === widgetId
    );

    if (!widgetToDelete) {
      showAlert({
        type: AlertType.Error,
        title: 'Error',
        text: 'Widget not found',
      });
      return;
    }
    setDeleteConfirmation({ isOpen: true, widget: widgetToDelete });
  };

  const [handleConfirmDelete, loading] = useStatefulApi(
    () => {
      const widgetToDelete = deleteConfirmation.widget;
      if (!widgetToDelete)
        return Promise.reject(new Error('No widget selected'));

      const apiUrl = generatePath(API_DASHBOARD_WIDGET_DELETE, {
        solutionId,
        dashboardId: item?.id || 'default',
        widgetId: widgetToDelete.id,
        twinId: item?.twinId,
      });

      return pepApiRequest({
        method: RequestMethod.Delete,
        url: apiUrl,
      });
    },
    (response) => {
      const widgetToDelete = deleteConfirmation.widget;
      if (!widgetToDelete) return;

      setDashboardState((prev) => {
        const newWidgets = prev.widgets.filter(
          (w) => w.id !== widgetToDelete.id
        );
        return {
          ...prev,
          widgets: newWidgets,
          isEditing: newWidgets.length === 0 ? false : prev.isEditing,
        };
      });

      if (solutionId && item?.id) {
        actions.WidgetList({
          solutionId,
          dashboardId: item.id,
          twinId: item?.twinId,
        });
      }

      showAlert({
        type: AlertType.Success,
        title: 'Widget Deleted',
        text: `Widget "${widgetToDelete.title}" has been deleted successfully`,
      });

      setDeleteConfirmation({ isOpen: false });
    },
    (error) => {
      console.error('Delete API error:', error);
      showAlert({
        type: AlertType.Error,
        title: 'Delete Failed',
        text: 'Failed to delete widget. Please try again.',
      });
    }
  );

  const handleDeleteModalClose: ModalCloseHandler = (event, reason) => {
    if (reason === ModalCloseReason.completeAction) {
      return;
    }
    setDeleteConfirmation({ isOpen: false });
  };

  const handleResizeWidget = (
    widgetId: string,
    newSize: { w: number; h: number }
  ) => {
    setDashboardState((prev) => ({
      ...prev,
      widgets: prev.widgets.map((widget) =>
        widget.id === widgetId
          ? {
            ...widget,
            layout: { ...widget.layout, width: newSize.w, height: newSize.h },
          }
          : widget
      ),
    }));

    setUnsavedWidgets((prev) => {
      const newSet = new Set(prev);
      newSet.add(widgetId);
      return newSet;
    });
  };

  const sortedWidgets = [...dashboardState.widgets].sort(
    (a, b) => (a.layout.order || 0) - (b.layout.order || 0)
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.paper',
        color: 'text.primary',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          p: 1,
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            onClick={handleAddWidget}
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
          >
            Add Widget
          </Button>

          <Button
            onClick={toggleEditMode}
            variant="outlined"
            color="primary"
            size="small"
            disabled={dashboardState.widgets.length === 0}
            startIcon={dashboardState.isEditing ? <CloseIcon /> : <EditIcon />}
          >
            {dashboardState.isEditing ? 'Exit Edit' : 'Edit Mode'}
          </Button>
        </Stack>
      </Box>

      <Container sx={{ py: 3 }}>
        {dashboardState.widgets.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '400px',
            }}
          >
            <Box textAlign="center">
              <Typography variant="h1" sx={{ mb: 2 }}>
                📊
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                No widgets yet
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Add your first widget to get started
              </Typography>
              <Button
                onClick={handleAddWidget}
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
              >
                Add Widget
              </Button>
            </Box>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {sortedWidgets.map((widget) => (
              <Grid
                key={widget.id}
                item
                xs={widget.layout.width}
                sx={{
                  minHeight: `${widget.layout.height * 50}px`,
                }}
              >
                <ResizableWidget
                  widget={widget}
                  onResize={handleResizeWidget}
                  isEditing={dashboardState.isEditing}
                >
                  <WidgetRenderer
                    widget={widget}
                    onEdit={handleEditWidget}
                    onRemove={handleRemoveWidget}
                    onSave={handleSaveWidgetLayout}
                    hasUnsavedChanges={unsavedWidgets.has(widget.id)}
                    isEditing={dashboardState.isEditing}
                  />
                </ResizableWidget>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <ReusableWidgetConfigModal
        isOpen={configModal.isOpen}
        onClose={() => setConfigModal({ isOpen: false })}
        onSave={handleSaveWidget}
        widget={configModal.widget}
        existingWidgets={dashboardState.widgets}
        dashboardId={item?.id}
        twinId={item?.twinId}
      />

      <ModalDialog
        open={deleteConfirmation.isOpen}
        title="Delete Widget"
        loading={loading}
        onClose={handleDeleteModalClose}
        onConfirm={handleConfirmDelete}
        cancelBtnText="No"
        confirmBtnText="Yes"
        maxWidth="sm"
        fullWidth
      >
        Are you sure you want to delete the widget?
        <br />
        <b>{deleteConfirmation.widget?.title}</b>
      </ModalDialog>
      {widgetListLoading && <PageCardLoader />}
    </Box>
  );
};
